import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, setStoredToken, formatApiError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { ArrowRight, Delete } from 'lucide-react';
import { Logo } from '../components/Logo';

const CODE_LENGTH = 6;
const STEPS = [
    { key: 'restaurant', label: 'Cod restaurant', testId: 'pin-restaurant' },
    { key: 'staff', label: 'ID angajat', testId: 'pin-staff' },
    { key: 'pin', label: 'PIN', testId: 'pin-pin' },
];

export default function PinLogin() {
    const { refresh } = useAuth();
    const nav = useNavigate();
    const [step, setStep] = useState(0);
    const [values, setValues] = useState({ restaurant: '', staff: '', pin: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const stepKey = STEPS[step].key;
    const currentValue = values[stepKey];

    const submit = async (payload) => {
        setError('');
        setLoading(true);
        try {
            const { data } = await api.post('/auth/pin-login', payload);
            if (data.access_token) setStoredToken(data.access_token);
            await refresh();
            const role = data.user.role;
            nav(role === 'kitchen' ? '/r/kitchen' : '/r', { replace: true });
        } catch (err) {
            setError(formatApiError(err));
            setValues({ restaurant: '', staff: '', pin: '' });
            setStep(0);
        } finally {
            setLoading(false);
        }
    };

    const press = (n) => {
        if (currentValue.length >= CODE_LENGTH || loading) return;
        const next = currentValue + n;
        const nextValues = { ...values, [stepKey]: next };
        setValues(nextValues);
        if (next.length === CODE_LENGTH) {
            if (step < STEPS.length - 1) {
                setStep(step + 1);
            } else {
                submit({
                    restaurant_code: nextValues.restaurant,
                    staff_code: nextValues.staff,
                    pin: nextValues.pin,
                });
            }
        }
    };

    const back = () => {
        if (currentValue.length > 0) {
            setValues({ ...values, [stepKey]: currentValue.slice(0, -1) });
            return;
        }
        if (step > 0) setStep(step - 1);
    };

    const reset = () => {
        setValues({ restaurant: '', staff: '', pin: '' });
        setStep(0);
        setError('');
    };

    return (
        <div className="grain" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem' }}>
            <div style={{ width: '100%', maxWidth: 420 }} className="fade-in-up">
                <div style={{ marginBottom: '1.4rem' }}>
                    <Logo size="lg" />
                </div>

                <h1 className="headline" style={{ fontSize: '2.2rem', margin: 0 }}>Login PIN rapid</h1>
                <p style={{ color: 'var(--text-muted)', marginBottom: '1.4rem' }}>
                    Introdu codul restaurantului, ID-ul tău de angajat și PIN-ul de 6 cifre.
                </p>

                <div className="panel" style={{ padding: '1.6rem' }} data-testid="pin-login-panel">
                    <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
                        {STEPS.map((s, i) => (
                            <div
                                key={s.key}
                                style={{
                                    flex: 1,
                                    padding: '0.55rem 0.35rem',
                                    borderRadius: 8,
                                    textAlign: 'center',
                                    fontSize: '0.72rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                    border: `1px solid ${i === step ? 'var(--amber)' : 'var(--border)'}`,
                                    color: i === step ? 'var(--amber)' : 'var(--text-muted)',
                                    background: i < step ? 'rgba(245,158,11,0.08)' : 'transparent',
                                }}
                            >
                                {s.label}
                            </div>
                        ))}
                    </div>

                    <div style={{ textAlign: 'center', marginBottom: '0.35rem', color: 'var(--text-muted)', fontSize: '0.88rem' }}>
                        {STEPS[step].label}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: '1.4rem' }} data-testid={STEPS[step].testId}>
                        {Array.from({ length: CODE_LENGTH }).map((_, i) => (
                            <div
                                key={i}
                                style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: 99,
                                    background: currentValue.length > i ? 'var(--amber)' : 'rgba(255,255,255,0.08)',
                                    border: currentValue.length > i ? '1px solid var(--amber)' : '1px solid var(--border)',
                                    transition: 'all 0.2s',
                                    boxShadow: currentValue.length > i ? '0 0 12px rgba(245,158,11,0.5)' : 'none',
                                }}
                            />
                        ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((n) => (
                            <button
                                key={n}
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => press(n)}
                                style={{ height: 64, fontSize: '1.6rem', fontWeight: 600 }}
                                disabled={loading}
                                data-testid={`pin-key-${n}`}
                            >
                                {n}
                            </button>
                        ))}
                        <button type="button" className="btn btn-ghost" onClick={back} style={{ height: 64 }} disabled={loading} data-testid="pin-back">
                            <Delete size={20} />
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => press('0')}
                            style={{ height: 64, fontSize: '1.6rem', fontWeight: 600 }}
                            disabled={loading}
                            data-testid="pin-key-0"
                        >
                            0
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                if (values.restaurant.length === CODE_LENGTH && values.staff.length === CODE_LENGTH && values.pin.length === CODE_LENGTH) {
                                    submit({
                                        restaurant_code: values.restaurant,
                                        staff_code: values.staff,
                                        pin: values.pin,
                                    });
                                }
                            }}
                            disabled={loading || values.pin.length < CODE_LENGTH}
                            style={{ height: 64 }}
                            data-testid="pin-submit"
                        >
                            {loading ? <span className="spinner" /> : <ArrowRight size={20} />}
                        </button>
                    </div>

                    {error && (
                        <div style={{ color: 'var(--neon-magenta)', fontSize: '0.88rem', marginTop: '0.9rem', textAlign: 'center' }} data-testid="pin-error">
                            {error}
                        </div>
                    )}

                    <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={reset} data-testid="pin-reset">
                            Resetează
                        </button>
                    </div>

                    <div style={{ marginTop: '1.2rem', padding: '0.7rem', background: 'rgba(0,217,255,0.06)', border: '1px solid rgba(0,217,255,0.2)', borderRadius: '0.6rem', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                        <strong style={{ color: 'var(--neon-cyan)' }}>Securitate</strong>: Codurile le primești de la administratorul restaurantului. Fără ele, loginul nu este posibil.
                    </div>
                </div>

                <div style={{ textAlign: 'center', marginTop: '1.2rem', color: 'var(--text-muted)' }}>
                    <Link to="/login" style={{ color: 'var(--amber)' }} data-testid="goto-email-login">Login cu email și parolă</Link>
                </div>
            </div>
        </div>
    );
}
